import {
    Course,
    CourseMetaDataQuery,
    CoursesWithActivitiesQuery,
    CourseTerm,
    CourseUnitActivitiesDataQuery,
    CourseUnitActivitiesDataQueryVariables,
    CourseUnitsDataQuery,
    CourseUnitsDataQueryVariables,
    Enrollment,
    EnrollmentsOnlyQuery,
    Maybe,
    Role,
    useCourseMetaDataQuery,
    useCoursesRolesDataQuery,
    useCoursesRolesQuery,
    useCoursesWithActivitiesQuery,
    useCourseTermsDataQuery,
    useCourseUnitActivitiesDataQuery,
    useCourseUnitsDataQuery,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useUserSession } from '@hooks/user/useDefaultSession';
import { useQueryParams } from '@hooks/useQueryParams';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { useCourseStateContext, useCourseTermContext } from '@components/context/CourseContext';
import { useCourseTermsStore } from './useCourseTerms';

type CoursePermissionType = {
    currentRole: Enrollment | null;
    currentRolePermissions: Set<string>;
    otherEnrollments: Enrollment[];
} | null;

export function useCourseLevelPermissions(
    queryResponse: UseQueryResult<
        NonNullable<CourseMetaDataQuery['courses']>[0] | undefined,
        unknown
    >,
) {
    const [permissions, setPermissions] = useState<CoursePermissionType>(null);
    const { id } = useQueryParams(['id']);
    const [user] = useUserSession();
    const router = useRouter();

    useEffect(() => {
        if (queryResponse.isLoading) return;

        if (
            queryResponse?.data === undefined ||
            (queryResponse?.data as any)?.courses?.length === 0
        ) {
            if (router.asPath.toLowerCase().includes('courses')) {
                router.replace('/');
                return setPermissions(null);
            }
        }

        let currentRole: Enrollment | null = null;
        let currentRolePermissions = new Set<string>();
        if (queryResponse?.data?.currentEnrollment?.role?.permissions) {
            currentRole = (queryResponse?.data?.currentEnrollment as Enrollment) ?? null;
            currentRolePermissions = new Set<string>(
                queryResponse?.data?.currentEnrollment?.role?.permissions,
            );
        }
        const duplicates = new Set<string>();
        let otherEnrollments: Enrollment[] = [];
        queryResponse.data?.enrollments?.forEach((e: any) => {
            if (!duplicates.has(e?.role?.name) && currentRole?.role?.id != e?.role?.id) {
                otherEnrollments.push(e);
            }
            duplicates.add(e?.role?.name);
        });
        setPermissions({ currentRole, currentRolePermissions, otherEnrollments });
    }, [queryResponse?.data, user, id, router, queryResponse?.isLoading]);

    const isCourseRole = useCallback(
        (courseRole: Role) => {
            return permissions?.currentRole?.role?.type === courseRole;
        },
        [permissions],
    );

    const hasCoursePermission = useCallback(
        (permission: string) => {
            return permissions?.currentRolePermissions?.has(permission) ?? false;
        },
        [permissions],
    );

    return {
        isLoading: queryResponse.isLoading,
        initialized: permissions !== null,
        permissions,
        isCourseRole,
        hasCoursePermission,
        user,
    };
}

export function useCourseMetaData() {
    const { id } = useQueryParams(['id']);
    const [user] = useUserSession();

    return useCourseMetaDataQuery(
        getClient('courses'),
        {
            ids: [id],
            userId: user?.id,
        },
        {
            enabled: !!id && !!user?.id,
            select: (data) => {
                return data?.courses?.[0];
            },
            onSuccess: (data) => {},
            retry: 2,
            staleTime: 1000 * 60 * 1,
            // This item is used across the site. It shouldnt be removed anytime soon but should be invalidated semi-often
            cacheTime: 1000 * 60 * 60,
            notifyOnChangeProps: ['data', 'isError', 'isLoading'],
        },
    );
}

export function useCourseUnitsData<TData = CourseUnitsDataQuery, TError = unknown>({
    variables,
    options,
}: {
    variables: Pick<CourseUnitsDataQueryVariables, 'includeHidden' | 'unitIds' | 'courseTermId'>;
    options?: UseQueryOptions<CourseUnitsDataQuery, TData, TError>;
}) {
    const { id } = useQueryParams(['id']);
    const [user] = useUserSession();
    return useCourseUnitsDataQuery(
        getClient('courses'),
        {
            ids: [id],
            userId: user?.id,
            includeActivities: true,
            ...(variables ?? {}),
        },
        options,
    );
}

export function useCourseUnitsFixed({
    variables,
}: {
    variables?: Pick<
        CourseUnitsDataQueryVariables,
        | 'unitIds'
        | 'courseTermId'
        | 'includeHidden'
        | 'includeDraft'
        | 'includeActivities'
        | 'onlyVisible'
    >;
}) {
    const { data } = useCourseStateContext();
    const { currentSelectedTermId, termList } = useCourseTermContext();
    return useCourseUnitsData({
        variables: {
            courseTermId: currentSelectedTermId,
            ...(variables ?? {}),
        },
        options: {
            enabled: !!data,
            select: (data) => {
                return data?.courses?.[0]?.units;
            },
            staleTime: 1000 * 60,
        },
    });
}

export function useCourseActivitiesData<TData = CourseUnitActivitiesDataQuery, TError = unknown>({
    variables,
    options,
}: {
    variables: Pick<
        CourseUnitActivitiesDataQueryVariables,
        | 'includeHidden'
        | 'unitIds'
        | 'courseTermId'
        | 'activityIds'
        | 'includeHiddenQuestions'
        | 'onlyVisible'
        | 'includeDraft'
    >;
    options?: UseQueryOptions<CourseUnitActivitiesDataQuery, TData, TError>;
}) {
    const { id } = useQueryParams(['id']);
    const [user] = useUserSession();
    return useCourseUnitActivitiesDataQuery(
        getClient('courses'),
        {
            ids: [id],
            userId: user?.id,
            ...variables,
        },
        options,
    );
}

export function useCourseActivitiesFixed({
    variables,
}: {
    variables?: Pick<
        CourseUnitActivitiesDataQueryVariables,
        | 'includeHidden'
        | 'unitIds'
        | 'courseTermId'
        | 'activityIds'
        | 'includeHiddenQuestions'
        | 'includeDraft'
        | 'onlyVisible'
    >;
}) {
    const { data } = useCourseStateContext();

    return useCourseActivitiesData({
        variables: {
            ...(variables ?? {}),
        },
        options: {
            enabled: !!data,
            select: (data) => {
                return data?.courses?.[0]?.units ?? [];
            },
            staleTime: 1000 * 60 * 5,
            notifyOnChangeProps: 'tracked',
        },
    });
}

export function useCourseTermsDataFixed() {
    const { id } = useQueryParams(['id']);
    const [user] = useUserSession();
    return useCourseTermsDataQuery(
        getClient('courses'),
        {
            ids: [id],
            userId: user?.id,
        },
        {
            enabled: !!id,
            select: (data) => {
                return data?.courses?.[0];
            },
        },
    );
}

export function useCourseRolesDataFixed() {
    const { id } = useQueryParams(['id']);
    const [user] = useUserSession();

    return useCoursesRolesDataQuery(
        getClient('courses'),
        {
            ids: [id],
            userId: user?.id,
        },
        {
            enabled: !!id,
            select: (data) => {
                return data?.courses?.[0]?.roles;
            },
        },
    );
}
